import { Typography } from '@mui/material';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { array, object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import { FormikCodeField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { useVerifyEmail } from '@hooks/api/session';

import { HorizontalRule } from '@layouts/AuthLayout';

import {
  RecordsUpdateStyle,
  TitleText,
} from '../TaskModalContent/TaskModalContent.style';

const messages = defineMessages({
  verify: {
    id: 'changeEmail.verify',
    defaultMessage: 'Verify New Email',
  },
  title: {
    id: 'changeEmail.title',
    defaultMessage: 'Change Email',
  },
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  verifyText: {
    id: 'changeEmail.verifyText',
    defaultMessage:
      'A 6-digit code has been sent to {destination} via {medium}. Enter the code below for verification.',
  },
});

type FormValues = {
  code: string[];
  submit: string | null;
};

interface VerifyEmailModalContentProps {
  onSubmit: () => void;
  medium: string;
  destination: string;
}

export default function VerifyEmailModalContent({
  onSubmit,
  destination,
  medium,
}: VerifyEmailModalContentProps) {
  const { formatMessage } = useIntl();
  const [verifyEmail, verifyEmailLoading] = useVerifyEmail();

  const loading = verifyEmailLoading;

  const handleClose = () => onSubmit();

  const initialValues: FormValues = {
    code: ['', '', '', '', '', ''],
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    code: array().of(string().required(formatMessage(messages.requiredText))),
  });

  async function handleSubmit(values: FormValues) {
    const { errors } = await verifyEmail({
      input: {
        confirmationCode: values.code.join(''),
      },
    });
    if (!errors) {
      handleClose();
    }
  }

  return (
    <RecordsUpdateStyle>
      <TitleText>{formatMessage(messages.verify)}</TitleText>
      <Typography>
        {formatMessage(messages.verifyText, { medium, destination })}
      </Typography>
      <HorizontalRule />
      <ContentLoader lineCount={1} loading={loading}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <Field name="code">
                {({ field }: FieldProps<string>) => (
                  <FormikCodeField
                    {...field}
                    autoSubmit={false}
                    codeLength={values.code.length}
                  />
                )}
              </Field>

              <RoundedButton disabled={isSubmitting} type="submit">
                {formatMessage(messages.title)}
              </RoundedButton>
            </Form>
          )}
        </Formik>
      </ContentLoader>
    </RecordsUpdateStyle>
  );
}
